import { useLingui as _useLingui } from "../lib/i18n/useLingui";
import * as SheetPrimitive from "@radix-ui/react-dialog";
import { FocusScope } from "@radix-ui/react-focus-scope";
import { Button } from "../components/Button";
import { Icon } from "../components/Icon";
import { ScrollArea } from "../components/ScrollArea";
import { SheetViewportProvider } from "../components/SheetViewportContext";
import { XClose } from "../icons/v2-stroke";
import { cn } from "../lib/utils";
import { cva } from "class-variance-authority";
import * as React from "react";

/**
 * A panel that slides in from an edge of the screen, built on Radix Dialog. Compose it
 * from `SheetTrigger`, `SheetContent` (with a `side` and `size`), `SheetHeader`
 * (`SheetTitle`, `SheetDescription`), `SheetContainer` for the scrollable body, and
 * `SheetFooter`. Use it for secondary tasks, detail views, or forms that need more room
 * than a popover but shouldn't navigate away. For a multi-step flow use `MultiPageSheet`;
 * for a focus-blocking centered modal, use `Dialog`.
 *
 * @summary Edge-anchored sliding panel.
 */
const Sheet = SheetPrimitive.Root;
const SheetTrigger = SheetPrimitive.Trigger;
const SheetClose = SheetPrimitive.Close;
const SheetPortal = SheetPrimitive.Portal;
const SheetOverlay = React.forwardRef(({
  className,
  ...props
}, ref) => /*#__PURE__*/React.createElement(SheetPrimitive.Overlay, {
  className: cn("fixed inset-0 z-50", "bg-muted-foreground/75 dark:bg-muted-background/75", "data-[state=open]:animate-in data-[state=closed]:animate-out", "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0", "duration-300 ease-out-quint", className),
  ...props,
  ref: ref
}));
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;
const SHEET_SIZES = ["md", "lg", "xl", "2xl", "3xl"];
const SHEET_SIDES = ["top", "bottom", "left", "right"];
const sizeClasses = {
  md: "sm:max-w-md",
  lg: "sm:max-w-xl",
  xl: "sm:max-w-3xl",
  "2xl": "sm:max-w-4xl",
  "3xl": "sm:max-w-5xl"
};
const sheetVariants = cva(cn("fixed z-50 overflow-hidden flex flex-col h-full w-full", "bg-modal-background", "data-[state=open]:animate-in data-[state=closed]:animate-out duration-300 ease-out-quint"), {
  variants: {
    side: {
      top: cn("inset-x-0 top-0 data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top", "border-b border-border"),
      bottom: cn("inset-x-0 bottom-0 data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom", "border-t border-border"),
      left: cn("inset-y-0 left-0 data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left", "border-r border-border"),
      right: cn("inset-y-0 right-0 data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right", "border-l border-border")
    },
    size: sizeClasses
  },
  defaultVariants: {
    side: "right",
    size: "md"
  }
});
// Notifications are rendered by sonner in their own portal, outside of the sheet: interacting with
// a toast (typically dismissing it) must not be treated as an interaction outside the sheet.
function isEventInsideNotification(event) {
  const target = event.detail.originalEvent.target;
  return target instanceof Element && !!target.closest("[data-sonner-toaster]");
}

/**
 * The sliding panel itself, rendered in a portal over a dimming overlay. Pick the edge
 * with `side` and the width with `size`; always give it a `SheetHeader` with a
 * `SheetTitle` for context and accessibility. Cmd/Ctrl+Enter triggers the footer button
 * marked `data-sheet-save`.
 *
 * @summary Sheet panel content.
 */
const SheetContent = React.forwardRef(({
  className,
  children,
  size,
  side,
  trapFocusScope,
  preventAutoFocusOnClose = true,
  preventAutoFocusOnOpen = true,
  onCloseAutoFocus,
  onOpenAutoFocus,
  onPointerDownOutside,
  onInteractOutside,
  ...props
}, ref) => {
  const handleCloseAutoFocus = React.useCallback(event => {
    if (preventAutoFocusOnClose) {
      event.preventDefault();
    }
    onCloseAutoFocus === null || onCloseAutoFocus === void 0 || onCloseAutoFocus(event);
  }, [preventAutoFocusOnClose, onCloseAutoFocus]);
  const handleOpenAutoFocus = React.useCallback(event => {
    if (preventAutoFocusOnOpen) {
      event.preventDefault();
    }
    onOpenAutoFocus === null || onOpenAutoFocus === void 0 || onOpenAutoFocus(event);
  }, [preventAutoFocusOnOpen, onOpenAutoFocus]);
  const handlePointerDownOutside = React.useCallback(event => {
    if (isEventInsideNotification(event)) {
      event.preventDefault();
      return;
    }
    onPointerDownOutside === null || onPointerDownOutside === void 0 || onPointerDownOutside(event);
  }, [onPointerDownOutside]);
  const handleInteractOutside = React.useCallback(event => {
    if (isEventInsideNotification(event)) {
      event.preventDefault();
      return;
    }
    onInteractOutside === null || onInteractOutside === void 0 || onInteractOutside(event);
  }, [onInteractOutside]);
  const onKeyDownCapture = React.useCallback(e => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      const root = e.currentTarget;
      const target = root.querySelector('[data-sheet-save="true"]:not(:disabled)');
      if (target) {
        e.preventDefault();
        target.click();
      }
    }
  }, []);
  return /*#__PURE__*/React.createElement(SheetPortal, null, /*#__PURE__*/React.createElement(SheetOverlay, null), /*#__PURE__*/React.createElement(FocusScope, {
    trapped: trapFocusScope,
    asChild: true
  }, /*#__PURE__*/React.createElement(SheetPrimitive.Content, {
    ref: ref,
    className: cn(sheetVariants({
      size,
      side
    }), className, "sheet text-foreground"),
    onCloseAutoFocus: handleCloseAutoFocus,
    onOpenAutoFocus: handleOpenAutoFocus,
    onPointerDownOutside: handlePointerDownOutside,
    onInteractOutside: handleInteractOutside,
    onKeyDownCapture: onKeyDownCapture,
    ...props
  }, children)));
});
SheetContent.displayName = SheetPrimitive.Content.displayName;
const SheetHeader = ({
  className,
  children,
  hideButton = false,
  ...props
}) => {
  const {
    _: _t
  } = _useLingui();
  return /*#__PURE__*/React.createElement("div", {
    className: cn("z-50 flex flex-none flex-col p-5 text-left", !hideButton && "pr-12", "bg-modal-background", className),
    ...props
  }, children, !hideButton && /*#__PURE__*/React.createElement(SheetClose, {
    asChild: true,
    className: "absolute right-3 top-4"
  }, /*#__PURE__*/React.createElement(Button, {
    icon: XClose,
    "aria-label": _t(
    /** i18n */
    {
      id: "yz7wBu",
      message: "Close"
    }),
    variant: "ghost",
    size: "sm"
  })));
};
SheetHeader.displayName = "SheetHeader";
const ScrollContainer = ({
  noScroll,
  className,
  children
}) => {
  const viewportRef = React.useRef(null);
  const [viewport, setViewport] = React.useState(null);
  React.useEffect(() => {
    setViewport(viewportRef.current);
  }, []);
  const value = React.useMemo(() => viewport, [viewport]);
  if (noScroll) {
    return /*#__PURE__*/React.createElement("div", {
      className: cn(className, "flex flex-col")
    }, children);
  }
  return /*#__PURE__*/React.createElement(ScrollArea, {
    className: className,
    viewportRef: viewportRef
  }, /*#__PURE__*/React.createElement(SheetViewportProvider, {
    value: value
  }, children));
};
const SheetContainer = ({
  children,
  noScroll,
  isListSelector,
  className
}) => {
  return /*#__PURE__*/React.createElement(ScrollContainer, {
    noScroll: noScroll,
    className: cn("min-h-0 w-full flex-1 overflow-hidden", "border-t border-border/60 transition-all duration-300")
  }, /*#__PURE__*/React.createElement("div", {
    className: cn("relative flex min-h-0 flex-1 flex-col overflow-hidden text-left text-sm text-foreground", !isListSelector && "gap-5 px-5 pt-3", className)
  }, children));
};
SheetContainer.displayName = "SheetContainer";
const SheetFooter = ({
  className,
  children,
  leftButtonProps,
  rightButtonProps,
  rightEndButtonProps,
  sheetCloseClassName,
  ...props
}) => {
  return /*#__PURE__*/React.createElement("div", {
    className: cn("flex flex-none flex-col gap-2", "border-border", className),
    ...props
  }, children, /*#__PURE__*/React.createElement("div", {
    className: "flex flex-row gap-2 border-t border-border p-3"
  }, leftButtonProps && (leftButtonProps.disabled ? /*#__PURE__*/React.createElement(Button, {
    ...leftButtonProps
  }) : /*#__PURE__*/React.createElement(SheetClose, {
    className: sheetCloseClassName,
    asChild: true
  }, /*#__PURE__*/React.createElement(Button, {
    ...leftButtonProps
  }))), /*#__PURE__*/React.createElement("div", {
    className: "flex-grow"
  }), rightButtonProps && (rightButtonProps.disabled ? /*#__PURE__*/React.createElement(Button, {
    "data-sheet-save": "true",
    ...rightButtonProps
  }) : /*#__PURE__*/React.createElement(SheetClose, {
    className: sheetCloseClassName,
    asChild: true
  }, /*#__PURE__*/React.createElement(Button, {
    "data-sheet-save": "true",
    ...rightButtonProps
  }))), rightEndButtonProps && (rightEndButtonProps.disabled ? /*#__PURE__*/React.createElement(Button, {
    ...rightEndButtonProps
  }) : /*#__PURE__*/React.createElement(SheetClose, {
    className: sheetCloseClassName,
    asChild: true
  }, /*#__PURE__*/React.createElement(Button, {
    ...rightEndButtonProps
  })))));
};
SheetFooter.displayName = "SheetFooter";
const SheetTitle = React.forwardRef(({
  className,
  icon,
  ...props
}, ref) => /*#__PURE__*/React.createElement(React.Fragment, null, icon && /*#__PURE__*/React.createElement(Icon, {
  visual: icon,
  size: "lg",
  className: "text-foreground"
}), /*#__PURE__*/React.createElement(SheetPrimitive.Title, {
  ref: ref,
  className: cn("heading-lg", className),
  ...props
})));
SheetTitle.displayName = SheetPrimitive.Title.displayName;
const SheetDescription = React.forwardRef(({
  className,
  ...props
}, ref) => /*#__PURE__*/React.createElement(SheetPrimitive.Description, {
  ref: ref,
  className: cn("text-sm", "text-muted-foreground", className),
  ...props
}));
SheetDescription.displayName = SheetPrimitive.Description.displayName;
export { SheetViewportProvider, useSheetViewport } from "../components/SheetViewportContext";
export { Sheet, SheetClose, SheetContainer, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetOverlay, SheetPortal, SheetTitle, SheetTrigger };
//# sourceMappingURL=Sheet.js.map